<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, provide, ref, watch, type CSSProperties } from 'vue'
import AppSelectField from '@/components/AppSelectField.vue'
import { NButton, NInput, NModal, NSwitch, useMessage } from 'naive-ui'
import NumberStepperField from '@/components/NumberStepperField.vue'
import ResourceSelectField from '@/features/virtual-resource/components/ResourceSelectField.vue'
import { measureBitmapFontText } from '@/features/bitmap-font/metrics'
import { getPreviewCenterOffset } from '@/features/preview/textLayout'
import { DIALOG_GRAPHIC_BASE_FOLDER } from '@/features/workspace/settings/settingsConstants'
import { resolveResourceUrlFromFileName } from '@/features/virtual-resource/fileNamePath'
import { useAuthStore } from '@/features/auth'
import BrowserUtil from '@/platform/oss/browserUtil'
import { useSceneEditorStore } from '@/store/sceneEditor'
import { useWorkspaceStore } from '@/store/workspace'
import TextImportResultRow from '@/features/text-import/TextImportResultRow.vue'
import {
  cloneAdvancedTextImportRow,
  cloneBasicTextImportRow,
  createTextImportVirtualRows,
  getAdvancedRowHeight,
  getBasicRowHeight,
  getTextImportVirtualListHeight,
  TEXT_IMPORT_ADVANCED_GRID_COLUMNS,
  TEXT_IMPORT_ADVANCED_TABLE_MIN_WIDTH,
  TEXT_IMPORT_BASIC_GRID_COLUMNS,
  TEXT_IMPORT_BASIC_TABLE_MIN_WIDTH,
  canAdvancedTextImportRowCopy,
  canAdvancedTextImportRowDelete,
  canAdvancedTextImportRowPasteAt,
  isAdvancedOptionStructuralRow,
} from '@/features/text-import/textImportResultList'
import {
  normalizeAdvancedTextImportRows,
  parseAdvancedTextImport,
  parseBasicTextImport,
  type AdvancedSymbolSettings,
  type AdvancedTextImportRow,
  type BasicTextImportRow,
  type TextImportMode,
} from '@/features/text-import/textImportLogic'
import { textImportRowContextKey } from '@/features/text-import/textImportRowContext'

const ADVANCED_STEP_LABELS = ['添加文本', '设置识别符号', '绑定立绘', '细节调整']
const TEXT_IMPORT_SAVE_NAME_DEFAULT = '橙易剧本'

const props = defineProps<{
  show: boolean
  portraitLayout?: boolean
}>()

const emit = defineEmits<{
  'update:show': [value: boolean]
}>()

const message = useMessage()
const editor = useSceneEditorStore()
const auth = useAuthStore()
const workspace = useWorkspaceStore()
const mode = ref<TextImportMode>('basic')
const basicText = ref('')
const basicRows = ref<BasicTextImportRow[]>([])
const basicShowResult = ref(false)
const basicNameIdentify = ref(true)
const rows = ref(2)
const columns = ref(20)
const advancedStep = ref(1)
const advancedText = ref('')
const advancedRows = ref<AdvancedTextImportRow[]>([])
const advancedScrollTop = ref(0)
const basicScrollTop = ref(0)
const selectedPersonName = ref('')
const lastSaveName = ref(TEXT_IMPORT_SAVE_NAME_DEFAULT)
const basicCloudFiles = ref<Array<{ label: string; value: string }>>([])
const advancedCloudFiles = ref<Array<{ label: string; value: string }>>([])
const selectedBasicCloudFile = ref('')
const selectedAdvancedCloudFile = ref('')
const cloudLoading = ref(false)
/** 初级/高级「完成」导入中，防止重复点击多次写入工程。 */
const importSubmitting = ref(false)
const txtFileInputRef = ref<HTMLInputElement | null>(null)
const saveNamePromptOpen = ref(false)
const scriptPickerOpen = ref(false)
const scriptPickerSelection = ref('')
const pendingSaveName = ref('')
const halfDragState = ref<{ offsetX: number; offsetY: number } | null>(null)
const talkImageSize = ref({ width: 0, height: 0 })
const nameImageSize = ref({ width: 0, height: 0 })
const previewContainerRef = ref<HTMLElement | null>(null)
const advancedPreviewContainerRef = ref<HTMLElement | null>(null)
const halfPreviewContainerRef = ref<HTMLElement | null>(null)
const previewContainerWidth = ref(0)
const halfImageSize = ref({ width: 0, height: 0 })
let previewResizeObserver: ResizeObserver | null = null
const selectedBasicRowId = ref('')
const selectedAdvancedRowId = ref('')
const clipboardBasicRow = ref<BasicTextImportRow | null>(null)
const clipboardAdvancedRow = ref<AdvancedTextImportRow | null>(null)
const advancedSettings = ref<AdvancedSymbolSettings>({
  namePrepend: '【',
  nameAppend: '】',
  nameWrap: true,
  optionRecognize: false,
  optionStart: '-开始',
  optionItem: '-选项：',
  optionEnd: '-结束',
  sceneRecognize: false,
  scenePrepend: '{',
  sceneAppend: '}',
  sceneWrap: true,
  rows: 2,
  columns: 20,
})

const currentMapName = computed(() => editor.selectedMap?.Name ?? '')
const currentMapId = computed(() => editor.selectedMap?.Id ?? 0)
const project = computed(() => workspace.project)
const projectGuid = computed(() => workspace.project?.projectHeader.ID?.trim() ?? '')
const orgtextFolderPath = computed(() => `${projectGuid.value}/current/orgtext`)
const projectScreenWidth = computed(() => Math.max(1, Number(project.value?.screenHash.width ?? 1280)))
const projectScreenHeight = computed(() => Math.max(1, Number(project.value?.screenHash.height ?? 720)))
const talkSetting = computed(() => project.value?.systemData?.Talk ?? null)
const totalSetting = computed(() => project.value?.systemData?.totalSetting ?? null)
const talkBoxUrl = computed(() => resolveGraphicPreviewUrl(talkSetting.value?.TalkWin.BackPic.name ?? ''))
const nameBoxUrl = computed(() => resolveGraphicPreviewUrl(talkSetting.value?.NameWinS.BackPic.name ?? ''))
const talkBoxWidth = computed(() => talkImageSize.value.width || Math.max(1, Number(talkSetting.value?.TalkWin.p_viewW ?? 720)))
const talkBoxHeight = computed(() => talkImageSize.value.height || Math.max(1, Number(talkSetting.value?.TalkWin.p_viewH ?? 180)))
const nameBoxWidth = computed(() => nameImageSize.value.width || 180)
const nameBoxHeight = computed(() => nameImageSize.value.height || 60)
const previewScale = computed(() => {
  if (!previewContainerWidth.value) return 0.2
  return Math.min(1, Math.max(0.1, previewContainerWidth.value / projectScreenWidth.value))
})
const previewTalkTop = computed(() => {
  const talk = talkSetting.value?.TalkWin
  return projectScreenHeight.value - Number(talk?.Y ?? 0) - talkBoxHeight.value
})
const previewNameTop = computed(() => {
  const name = talkSetting.value?.NameWinS
  return previewTalkTop.value + Number(name?.y ?? 0)
})
const previewVisibleTop = computed(() => Math.max(0, Math.min(previewTalkTop.value, previewNameTop.value) - 16))
const previewVisibleBottom = computed(() => {
  return Math.min(
    projectScreenHeight.value,
    Math.max(previewTalkTop.value + talkBoxHeight.value, previewNameTop.value + nameBoxHeight.value) + 16,
  )
})
const previewFrameStyle = computed<CSSProperties>(() => ({
  height: `${Math.ceil((previewVisibleBottom.value - previewVisibleTop.value) * previewScale.value)}px`,
}))
const previewText = computed(() => '橙光制作工具'.repeat(Math.ceil(columns.value / 6) + 1).slice(0, columns.value))
const previewFontFamily = computed(() => {
  const fontName = String(totalSetting.value?.FontName || '').trim()
  return fontName ? `"${fontName}", "Microsoft YaHei", sans-serif` : '"Microsoft YaHei", sans-serif'
})
const previewFontSize = computed(() => Math.max(12, Number(totalSetting.value?.FontSize ?? 30)))
const previewLineHeight = computed(() => Math.max(1, Math.round(Math.round(previewFontSize.value) * 1.5)))
const previewMessageColor = computed(() => `rgb(${String(totalSetting.value?.FontColor_MSG || '222, 226, 241')})`)
const previewNameTextMetrics = computed(() => {
  return measureBitmapFontText('姓名', previewFontSize.value, previewFontFamily.value)
})
const previewNameLineHeight = computed(() => Math.ceil(previewNameTextMetrics.value.height * 1.2))
const previewStageStyle = computed<CSSProperties>(() => ({
  width: `${projectScreenWidth.value}px`,
  height: `${projectScreenHeight.value}px`,
  top: `-${previewVisibleTop.value * previewScale.value}px`,
  transform: `translateX(-50%) scale(${previewScale.value})`,
}))
const halfPreviewFrameStyle = computed<CSSProperties>(() => ({
  height: `${Math.ceil(projectScreenHeight.value * previewScale.value)}px`,
}))
const halfPreviewStageStyle = computed<CSSProperties>(() => ({
  width: `${projectScreenWidth.value}px`,
  height: `${projectScreenHeight.value}px`,
  transform: `translateX(-50%) scale(${previewScale.value})`,
}))
const selectedHalfPreviewUrl = computed(() => {
  return resolveGraphicPreviewUrl(selectedPerson.value?.half.halfPath ?? '', '立绘')
})
const halfPreviewImageStyle = computed<CSSProperties>(() => ({
  left: `${Number(selectedPerson.value?.half.x ?? 0)}px`,
  top: `${Number(selectedPerson.value?.half.y ?? 0)}px`,
  width: `${halfImageSize.value.width || 240}px`,
  height: `${halfImageSize.value.height || 480}px`,
  cursor: selectedPerson.value ? 'move' : 'default',
}))
const previewTalkBoxStyle = computed<CSSProperties>(() => {
  const talk = talkSetting.value?.TalkWin
  return {
    left: `${Number(talk?.X ?? 0)}px`,
    top: `${previewTalkTop.value}px`,
    width: `${talkBoxWidth.value}px`,
    height: `${talkBoxHeight.value}px`,
  }
})
const previewNameBoxStyle = computed<CSSProperties>(() => {
  const talk = talkSetting.value?.TalkWin
  const name = talkSetting.value?.NameWinS
  return {
    left: `${Number(talk?.X ?? 0) + Number(name?.x ?? 0)}px`,
    top: `${previewNameTop.value}px`,
    width: `${nameBoxWidth.value}px`,
    height: `${nameBoxHeight.value}px`,
  }
})
const previewTalkTextStyle = computed<CSSProperties>(() => {
  const talk = talkSetting.value?.TalkWin
  return {
    left: `${Number(talk?.X ?? 0) + Number(talk?.p_x1 ?? 0)}px`,
    top: `${previewTalkTop.value + Number(talk?.p_y1 ?? 0)}px`,
    width: `${Math.max(1, Number(talk?.p_viewW ?? 0))}px`,
    height: `${Math.max(1, Number(talk?.p_viewH ?? 0))}px`,
    color: previewMessageColor.value,
    fontFamily: previewFontFamily.value,
    fontSize: `${previewFontSize.value}px`,
    lineHeight: `${previewLineHeight.value}px`,
  }
})
const previewNameTextStyle = computed<CSSProperties>(() => {
  const talk = talkSetting.value?.TalkWin
  const name = talkSetting.value?.NameWinS
  const boxLeft = Number(talk?.X ?? 0) + Number(name?.x ?? 0)
  const boxTop = previewNameTop.value
  const isCenter = Boolean(name?.IsCenter)
  const nameTextLeft = isCenter
    ? boxLeft + getPreviewCenterOffset(nameBoxWidth.value, previewNameTextMetrics.value.width)
    : boxLeft + Number(name?.P_x ?? 0)
  const nameTextTop = isCenter
    ? boxTop + getPreviewCenterOffset(nameBoxHeight.value, previewNameLineHeight.value)
    : boxTop + Number(name?.P_y ?? 0)
  return {
    left: `${nameTextLeft}px`,
    top: `${nameTextTop}px`,
    width: `${isCenter ? previewNameTextMetrics.value.width : Math.max(24, nameBoxWidth.value - Number(name?.P_x ?? 0))}px`,
    height: `${isCenter ? previewNameLineHeight.value : Math.max(18, nameBoxHeight.value - Number(name?.P_y ?? 0))}px`,
    color: previewMessageColor.value,
    fontFamily: previewFontFamily.value,
    fontSize: `${previewFontSize.value}px`,
    lineHeight: `${previewNameLineHeight.value}px`,
    textAlign: 'left',
  }
})
const basicTableTotalHeight = computed(() => getTextImportVirtualListHeight(basicRows.value, getBasicRowHeight))
const advancedTableTotalHeight = computed(() => getTextImportVirtualListHeight(advancedRows.value, getAdvancedRowHeight))
const basicVisibleRows = computed(() =>
  createTextImportVirtualRows(basicRows.value, basicScrollTop.value, getBasicRowHeight),
)
const advancedVisibleRows = computed(() =>
  createTextImportVirtualRows(advancedRows.value, advancedScrollTop.value, getAdvancedRowHeight),
)
const basicMapCount = computed(() => basicRows.value.filter((row) => row.mapName).length)
const advancedMapCount = computed(() => advancedRows.value.filter((row) => row.mapName).length)
const advancedPeople = computed(() => {
  const names = new Map<string, AdvancedTextImportRow['person']>()
  for (const row of advancedRows.value) {
    if (row.person.name && !names.has(row.person.name)) {
      names.set(row.person.name, row.person)
    }
  }
  return [...names.values()]
})
const selectedPerson = computed(() =>
  advancedPeople.value.find((person) => person.name === selectedPersonName.value) ?? advancedPeople.value[0] ?? null,
)
const halfPreviewNameText = computed(() => selectedPerson.value?.name || '姓名')
const halfPreviewNameTextMetrics = computed(() => {
  return measureBitmapFontText(halfPreviewNameText.value, previewFontSize.value, previewFontFamily.value)
})
const halfPreviewNameLineHeight = computed(() => Math.ceil(halfPreviewNameTextMetrics.value.height * 1.2))
const halfPreviewNameTextStyle = computed<CSSProperties>(() => {
  const talk = talkSetting.value?.TalkWin
  const name = talkSetting.value?.NameWinS
  const boxLeft = Number(talk?.X ?? 0) + Number(name?.x ?? 0)
  const boxTop = previewNameTop.value
  const isCenter = Boolean(name?.IsCenter)
  const nameTextLeft = isCenter
    ? boxLeft + getPreviewCenterOffset(nameBoxWidth.value, halfPreviewNameTextMetrics.value.width)
    : boxLeft + Number(name?.P_x ?? 0)
  const nameTextTop = isCenter
    ? boxTop + getPreviewCenterOffset(nameBoxHeight.value, halfPreviewNameLineHeight.value)
    : boxTop + Number(name?.P_y ?? 0)
  return {
    ...previewNameTextStyle.value,
    left: `${nameTextLeft}px`,
    top: `${nameTextTop}px`,
    width: `${isCenter ? halfPreviewNameTextMetrics.value.width : Math.max(24, nameBoxWidth.value - Number(name?.P_x ?? 0))}px`,
    height: `${isCenter ? halfPreviewNameLineHeight.value : Math.max(18, nameBoxHeight.value - Number(name?.P_y ?? 0))}px`,
    lineHeight: `${halfPreviewNameLineHeight.value}px`,
  }
})

function resolveGraphicPreviewUrl(storedPath: string, baseFolderPath = DIALOG_GRAPHIC_BASE_FOLDER): string {
  return resolveResourceUrlFromFileName(
    workspace.virtualResourceRoots.graphic,
    'graphic',
    storedPath,
    workspace.resourcePreviewOverrides,
    baseFolderPath,
  )
}

function handleTalkImageLoad(event: Event): void {
  const image = event.target as HTMLImageElement
  talkImageSize.value = {
    width: image.naturalWidth || image.width || 0,
    height: image.naturalHeight || image.height || 0,
  }
}

function handleNameImageLoad(event: Event): void {
  const image = event.target as HTMLImageElement
  nameImageSize.value = {
    width: image.naturalWidth || image.width || 0,
    height: image.naturalHeight || image.height || 0,
  }
}

function handleHalfImageLoad(event: Event): void {
  const image = event.target as HTMLImageElement
  halfImageSize.value = {
    width: image.naturalWidth || image.width || 0,
    height: image.naturalHeight || image.height || 0,
  }
  if (selectedPerson.value?.half.halfPath) {
    syncSelectedHalfPositionFromAlign()
  }
}

function updatePreviewContainerWidth(): void {
  previewContainerWidth.value = Math.max(
    previewContainerRef.value?.clientWidth ?? 0,
    advancedPreviewContainerRef.value?.clientWidth ?? 0,
    halfPreviewContainerRef.value?.clientWidth ?? 0,
  )
}

function observePreviewContainers(): void {
  previewResizeObserver?.disconnect()
  previewResizeObserver = null
  updatePreviewContainerWidth()
  previewResizeObserver = new ResizeObserver(updatePreviewContainerWidth)
  if (previewContainerRef.value) previewResizeObserver.observe(previewContainerRef.value)
  if (advancedPreviewContainerRef.value) previewResizeObserver.observe(advancedPreviewContainerRef.value)
  if (halfPreviewContainerRef.value) previewResizeObserver.observe(halfPreviewContainerRef.value)
}

onMounted(() => {
  observePreviewContainers()
})

onBeforeUnmount(() => {
  previewResizeObserver?.disconnect()
  previewResizeObserver = null
  handleHalfPreviewPointerUp()
  unbindTextImportKeydown()
})

watch(previewContainerRef, () => {
  observePreviewContainers()
})

watch(advancedPreviewContainerRef, () => {
  observePreviewContainers()
})

watch(halfPreviewContainerRef, () => {
  observePreviewContainers()
})

watch(selectedHalfPreviewUrl, () => {
  halfImageSize.value = { width: 0, height: 0 }
})

watch(selectedPerson, (person) => {
  selectedPersonName.value = person?.name ?? ''
})

watch(advancedStep, (step) => {
  if (step === 4) syncAdvancedSceneNameRows()
}, { immediate: true })

watch(() => props.show, (show) => {
  if (show) {
    bindTextImportKeydown()
    void nextTick(updatePreviewContainerWidth)
    if (advancedStep.value === 4) syncAdvancedSceneNameRows()
    void refreshCloudFiles()
    return
  }
  unbindTextImportKeydown()
})

function resetResultListScroll(): void {
  basicScrollTop.value = 0
  advancedScrollTop.value = 0
}

function handleBasicRowContext(row: BasicTextImportRow, action: 'copy' | 'paste' | 'delete'): void {
  if (action === 'copy') {
    clipboardBasicRow.value = cloneBasicTextImportRow(row)
    message.success('已复制')
    return
  }
  const index = basicRows.value.findIndex((item) => item.id === row.id)
  if (action === 'paste') {
    if (!clipboardBasicRow.value) {
      message.warning('请先复制一行')
      return
    }
    const pasted = cloneBasicTextImportRow(clipboardBasicRow.value)
    if (index >= 0) basicRows.value.splice(index + 1, 0, pasted)
    else basicRows.value.push(pasted)
    return
  }
  if (index < 0) return
  basicRows.value.splice(index, 1)
  if (selectedBasicRowId.value === row.id) selectedBasicRowId.value = ''
}

function hasTextImportDocumentSelection(): boolean {
  const selection = window.getSelection()
  return Boolean(selection && !selection.isCollapsed && selection.toString().length > 0)
}

function handleAdvancedRowContext(row: AdvancedTextImportRow, action: 'copy' | 'paste' | 'delete'): void {
  if (action === 'copy' && !canAdvancedTextImportRowCopy(row)) {
    message.warning('选项开始、选项、选项结束行不支持复制')
    return
  }
  if (action === 'delete' && !canAdvancedTextImportRowDelete(row)) {
    message.warning('选项开始、选项、选项结束行不支持删除')
    return
  }
  if (action === 'paste' && !canAdvancedTextImportRowPasteAt(row)) {
    message.warning('选项开始行不支持粘贴')
    return
  }
  if (action === 'copy') {
    clipboardAdvancedRow.value = cloneAdvancedTextImportRow(row)
    message.success('已复制')
    return
  }
  const index = advancedRows.value.findIndex((item) => item.uid === row.uid)
  if (action === 'paste') {
    if (!clipboardAdvancedRow.value) {
      message.warning('请先复制一行')
      return
    }
    if (isAdvancedOptionStructuralRow(clipboardAdvancedRow.value)) {
      message.warning('不能粘贴选项开始、选项、选项结束行')
      return
    }
    const pasted = cloneAdvancedTextImportRow(clipboardAdvancedRow.value)
    if (index >= 0) advancedRows.value.splice(index + 1, 0, pasted)
    else advancedRows.value.push(pasted)
    return
  }
  if (index < 0) return
  advancedRows.value.splice(index, 1)
  if (selectedAdvancedRowId.value === row.uid) selectedAdvancedRowId.value = ''
}

provide(textImportRowContextKey, {
  runRowAction(variant, row, action) {
    if (variant === 'basic') {
      handleBasicRowContext(row as BasicTextImportRow, action)
      return
    }
    handleAdvancedRowContext(row as AdvancedTextImportRow, action)
  },
})

/** 初级结果页或高级第四步。 */
const isTextImportResultStep = computed(() => {
  if (mode.value === 'basic') return basicShowResult.value
  return advancedStep.value === 4
})

/** 初级输入页或高级第一步，可粘贴/导入本地文本。 */
const isTextImportPasteStep = computed(() => {
  if (mode.value === 'basic') return !basicShowResult.value
  return advancedStep.value === 1
})

const currentCloudFiles = computed(() => (
  mode.value === 'basic' ? basicCloudFiles.value : advancedCloudFiles.value
))

/** 当前模式云盘是否已有可打开的剧本文件。 */
const canOpenCloudScript = computed(() => currentCloudFiles.value.length > 0)

function isTextImportEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  return target.isContentEditable
}

/** PC 端结果表快捷键：Ctrl/Cmd+C 复制、Ctrl/Cmd+V 粘贴、Delete 删除（需先选中一行）。 */
function handleTextImportKeydown(event: KeyboardEvent): void {
  if (!props.show || !isTextImportResultStep.value) return
  if (isTextImportEditableTarget(event.target)) return
  if (saveNamePromptOpen.value || scriptPickerOpen.value) return

  const withCommand = event.ctrlKey || event.metaKey
  const key = event.key.toLowerCase()

  if (mode.value === 'basic') {
    const row = basicRows.value.find((item) => item.id === selectedBasicRowId.value)
    if (!row) return
    if (withCommand && key === 'c') {
      event.preventDefault()
      handleBasicRowContext(row, 'copy')
      return
    }
    if (withCommand && key === 'v') {
      event.preventDefault()
      handleBasicRowContext(row, 'paste')
      return
    }
    if (event.key === 'Delete') {
      event.preventDefault()
      handleBasicRowContext(row, 'delete')
    }
    return
  }

  const row = advancedRows.value.find((item) => item.uid === selectedAdvancedRowId.value)
  if (!row) return
  if (withCommand && key === 'c') {
    if (isAdvancedOptionStructuralRow(row)) return
    if (hasTextImportDocumentSelection()) return
    event.preventDefault()
    handleAdvancedRowContext(row, 'copy')
    return
  }
  if (withCommand && key === 'v') {
    if (!canAdvancedTextImportRowPasteAt(row)) return
    event.preventDefault()
    handleAdvancedRowContext(row, 'paste')
    return
  }
  if (event.key === 'Delete') {
    if (!canAdvancedTextImportRowDelete(row)) return
    event.preventDefault()
    handleAdvancedRowContext(row, 'delete')
  }
}

function bindTextImportKeydown(): void {
  window.addEventListener('keydown', handleTextImportKeydown)
}

function unbindTextImportKeydown(): void {
  window.removeEventListener('keydown', handleTextImportKeydown)
}

function closeModal(): void {
  emit('update:show', false)
}

/** 打开本地 .txt 文件选择器。 */
function triggerTxtFileImport(): void {
  txtFileInputRef.value?.click()
}

/** 从本地 .txt 读取剧本文本，写入当前模式的输入框。 */
async function handleTxtFileChange(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  const isTxtName = /\.txt$/i.test(file.name)
  const isTextMime = !file.type || file.type === 'text/plain' || file.type.startsWith('text/')
  if (!isTxtName && !isTextMime) {
    message.warning('请选择 .txt 文本文件')
    return
  }
  try {
    const text = await file.text()
    if (mode.value === 'basic') {
      if (!isTextImportPasteStep.value) {
        message.warning('请先上一步再导入文本')
        return
      }
      basicText.value = text
    } else if (isTextImportPasteStep.value) {
      advancedText.value = text
    } else {
      message.warning('请在添加文本步骤使用导入文本')
      return
    }
    message.success(`已导入：${file.name}`)
  } catch {
    message.error('读取文本文件失败')
  }
}

function handleTextImportPrev(): void {
  if (mode.value === 'basic') {
    basicShowResult.value = false
    return
  }
  advancedStep.value = Math.max(1, advancedStep.value - 1)
}

function handleTextImportNext(): void {
  if (mode.value === 'basic') {
    handleBasicAnalyze()
    return
  }
  handleAdvancedNext()
}

function handleTextImportConfirm(): void {
  if (mode.value === 'basic') {
    void handleBasicFinish()
    return
  }
  void handleAdvancedFinish()
}

function resetTextImportDraft(): void {
  mode.value = 'basic'
  basicText.value = ''
  basicRows.value = []
  basicShowResult.value = false
  basicScrollTop.value = 0
  advancedStep.value = 1
  advancedText.value = ''
  advancedRows.value = []
  advancedScrollTop.value = 0
  selectedPersonName.value = ''
  selectedBasicCloudFile.value = ''
  selectedAdvancedCloudFile.value = ''
  lastSaveName.value = TEXT_IMPORT_SAVE_NAME_DEFAULT
  pendingSaveName.value = ''
  saveNamePromptOpen.value = false
  halfImageSize.value = { width: 0, height: 0 }
  importSubmitting.value = false
}

function getOssClient(): BrowserUtil | null {
  if (!projectGuid.value) {
    message.warning('当前工程缺少 GUID，无法操作云盘文本文件')
    return null
  }
  const oss = BrowserUtil.getInstance()
  if (auth.uid) {
    oss.connect(auth.uid)
  } else {
    oss.initDefaultConfig()
  }
  if (!oss.isConfigured()) {
    message.warning('OSS 未配置，请先登录后再使用云盘文本导入')
    return null
  }
  return oss
}

function normalizeOrgtextFileName(value: string): string {
  const name = value
    .trim()
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\.orgtext$/i, '')
  return `${name || TEXT_IMPORT_SAVE_NAME_DEFAULT}.orgtext`
}

type OrgtextPayload = {
  type?: string
  data?: BasicTextImportRow[]
  tableData?: AdvancedTextImportRow[]
  textContent?: string
  settings?: AdvancedSymbolSettings
}

/** 解析 `.orgtext` 文件 JSON 正文。 */
function parseOrgtextPayload(text: string): OrgtextPayload | null {
  try {
    const raw = text.replace(/^ORG_TEXT_IS_NEW\n/, '')
    return JSON.parse(raw) as OrgtextPayload
  } catch {
    return null
  }
}

function restoreCloudFileSelection(
  previousBasic: string,
  previousAdvanced: string,
  nextBasic: Array<{ label: string; value: string }>,
  nextAdvanced: Array<{ label: string; value: string }>,
): void {
  selectedBasicCloudFile.value = previousBasic && nextBasic.some((item) => item.value === previousBasic)
    ? previousBasic
    : (nextBasic[0]?.value ?? '')
  selectedAdvancedCloudFile.value = previousAdvanced && nextAdvanced.some((item) => item.value === previousAdvanced)
    ? previousAdvanced
    : (nextAdvanced[0]?.value ?? '')
}

/** 从 OSS `current/orgtext/` 拉取 `.orgtext` 列表，并按初级/高级格式分别归类。 */
async function refreshCloudFiles(): Promise<void> {
  const oss = getOssClient()
  if (!oss) {
    basicCloudFiles.value = []
    advancedCloudFiles.value = []
    selectedBasicCloudFile.value = ''
    selectedAdvancedCloudFile.value = ''
    return
  }
  cloudLoading.value = true
  try {
    await oss.createFolder(orgtextFolderPath.value)
    const files = await oss.listObjects(orgtextFolderPath.value)
    const previousBasic = selectedBasicCloudFile.value
    const previousAdvanced = selectedAdvancedCloudFile.value
    const candidates = files
      .filter((item) => item.relativePath && item.relativePath.toLowerCase().endsWith('.orgtext'))
      .map((item) => ({
        label: item.name,
        value: `${orgtextFolderPath.value}/${item.relativePath}`,
      }))
    const classified = await Promise.all(candidates.map(async (item) => {
      try {
        const text = await oss.getText(item.value)
        const payload = parseOrgtextPayload(text)
        return { item, type: payload?.type ?? '' }
      } catch {
        return { item, type: '' }
      }
    }))
    basicCloudFiles.value = classified
      .filter((entry) => entry.type === 'text')
      .map((entry) => entry.item)
    advancedCloudFiles.value = classified
      .filter((entry) => entry.type === 'advanced')
      .map((entry) => entry.item)
    restoreCloudFileSelection(previousBasic, previousAdvanced, basicCloudFiles.value, advancedCloudFiles.value)
  } catch (error) {
    message.error(error instanceof Error ? error.message : '读取云盘文本列表失败')
  } finally {
    cloudLoading.value = false
  }
}

function syncAdvancedRowsAndColumns(): void {
  advancedSettings.value.rows = rows.value
  advancedSettings.value.columns = columns.value
}

function handleBasicAnalyze(): void {
  if (!basicText.value.trim()) {
    message.warning('请输入文本内容…')
    return
  }
  if (!currentMapId.value || !currentMapName.value) {
    message.warning('请先选择要导入的剧情')
    return
  }
  basicRows.value = parseBasicTextImport(basicText.value, {
    rows: rows.value,
    columns: columns.value,
    nameIdentify: basicNameIdentify.value,
    mapId: currentMapId.value,
    mapName: currentMapName.value,
  })
  resetResultListScroll()
  selectedBasicRowId.value = basicRows.value[0]?.id ?? ''
  basicShowResult.value = true
}

async function handleBasicFinish(): Promise<void> {
  if (importSubmitting.value) return
  if (!basicRows.value.length) {
    message.warning('请先分析文本')
    return
  }
  importSubmitting.value = true
  try {
    const result = editor.importBasicTextRows(basicRows.value)
    if (!result) {
      message.error('文本导入失败，请先选择剧情')
      return
    }
    try {
      await workspace.saveProject()
    } catch (error) {
      message.error(error instanceof Error ? error.message : '导入后保存工程失败')
      return
    }
    message.success(`导入完成：新增 ${result.insertedEvents} 条文本，${result.createdMaps} 个剧情`)
    resetTextImportDraft()
    closeModal()
  } finally {
    importSubmitting.value = false
  }
}

function buildBasicPayload(): string {
  return `ORG_TEXT_IS_NEW\n${JSON.stringify({ type: 'text', data: basicRows.value })}`
}

function buildAdvancedPayload(): string {
  return `ORG_TEXT_IS_NEW\n${JSON.stringify({
    type: 'advanced',
    tableData: advancedRows.value,
    textContent: advancedText.value,
    settings: advancedSettings.value,
  }, null, 2)}`
}

function handleAdvancedNext(): void {
  if (advancedStep.value === 1 && !advancedText.value.trim()) {
    message.warning('请输入文本内容')
    return
  }
  if (advancedStep.value === 2) {
    syncAdvancedRowsAndColumns()
    const error = validateAdvancedSettings()
    if (error) {
      message.warning(error)
      return
    }
    const result = parseAdvancedTextImport(advancedText.value, advancedSettings.value)
    if (result.error) {
      message.warning(result.error)
      return
    }
    advancedRows.value = normalizeAdvancedTextImportRows(result.rows)
    syncAdvancedSceneNameRows()
    resetResultListScroll()
    selectedAdvancedRowId.value = advancedRows.value[0]?.uid ?? ''
  }
  if (advancedStep.value === 3) {
    resetResultListScroll()
  }
  advancedStep.value = Math.min(4, advancedStep.value + 1)
}

async function handleAdvancedFinish(): Promise<void> {
  if (importSubmitting.value) return
  if (!advancedRows.value.length) {
    message.warning('请先完成文本分析')
    return
  }
  const error = validateAdvancedRows()
  if (error) {
    message.warning(error)
    return
  }
  importSubmitting.value = true
  try {
    const result = editor.importAdvancedTextRows(advancedRows.value)
    if (!result) {
      message.error('文本导入失败，请先选择剧情')
      return
    }
    try {
      await workspace.saveProject()
    } catch (error) {
      message.error(error instanceof Error ? error.message : '导入后保存工程失败')
      return
    }
    message.success(`导入完成：新增 ${result.insertedEvents} 条文本，${result.createdMaps} 个剧情`)
    resetTextImportDraft()
    closeModal()
  } finally {
    importSubmitting.value = false
  }
}

function validateAdvancedSettings(): string {
  const settings = advancedSettings.value
  if (!settings.namePrepend || !settings.nameAppend) return '存在未设置的姓名识别符'
  if (settings.optionRecognize && (!settings.optionStart || !settings.optionItem || !settings.optionEnd)) {
    return '存在未设置的选项识别符'
  }
  if (settings.sceneRecognize && (!settings.scenePrepend || !settings.sceneAppend)) {
    return '存在未设置的剧情识别符'
  }
  return ''
}

function validateAdvancedRows(): string {
  const stack: string[] = []
  for (const row of advancedRows.value) {
    if (row.optionKey.type === 'start') stack.push('start')
    if (row.optionKey.type === 'end') stack.pop()
    if (stack.length && row.mapName) return '选项内不可设置剧情'
  }
  return ''
}

function getSceneNameFromAdvancedText(value: string): { name: string; rest: string } | null {
  const settings = advancedSettings.value
  if (!settings.scenePrepend || !settings.sceneAppend) return null
  const text = value.trim()
  if (!text.startsWith(settings.scenePrepend)) return null
  const endIndex = text.indexOf(settings.sceneAppend, settings.scenePrepend.length)
  if (endIndex < 0) return null
  const name = text.slice(settings.scenePrepend.length, endIndex).trim()
  if (!name) return null
  return {
    name,
    rest: text.slice(endIndex + settings.sceneAppend.length).trim(),
  }
}

function syncAdvancedSceneNameRows(): void {
  for (const row of advancedRows.value) {
    if (row.optionKey.type || row.mapName.trim()) continue
    const scene = getSceneNameFromAdvancedText(row.textContent)
    if (!scene) continue
    row.mapName = scene.name
    row.textContent = scene.rest
  }
}

function updateAdvancedTextContent(row: AdvancedTextImportRow, value: string): void {
  row.textContent = value
  if (row.optionKey.type || row.mapName.trim()) return
  const scene = getSceneNameFromAdvancedText(value)
  if (!scene) return
  row.mapName = scene.name
  row.textContent = scene.rest
}

function getHalfPreviewWidth(): number {
  return Math.max(1, halfImageSize.value.width || 240)
}

function getHalfPreviewHeight(): number {
  return Math.max(1, halfImageSize.value.height || 480)
}

/** 按左/中/右与立绘尺寸，同步当前选中角色的 halfId、x、y。 */
function syncSelectedHalfPositionFromAlign(halfId?: number): void {
  const person = selectedPerson.value
  if (!person) return
  const targetHalfId = halfId ?? person.half.halfId
  const imageWidth = getHalfPreviewWidth()
  const imageHeight = getHalfPreviewHeight()
  const nextX = targetHalfId === 4
    ? Math.max(0, projectScreenWidth.value - imageWidth)
    : targetHalfId === 5
      ? Math.max(0, Math.round((projectScreenWidth.value - imageWidth) / 2))
      : 0
  const nextY = Math.max(0, projectScreenHeight.value - imageHeight)
  for (const row of advancedRows.value) {
    if (row.person.name === person.name) {
      row.person.half.halfId = targetHalfId
      row.person.half.x = nextX
      row.person.half.y = nextY
    }
  }
}

function updateSelectedHalfPath(value: string): void {
  const person = selectedPerson.value
  if (!person) return
  for (const row of advancedRows.value) {
    if (row.person.name === person.name) row.person.half.halfPath = value
  }
  if (!value.trim()) return
  syncSelectedHalfPositionFromAlign()
}

function updateSelectedHalfPosition(halfId: number): void {
  syncSelectedHalfPositionFromAlign(halfId)
}

function updateSelectedHalfNumber(key: 'x' | 'y', value: number | null): void {
  const person = selectedPerson.value
  if (!person) return
  for (const row of advancedRows.value) {
    if (row.person.name === person.name) row.person.half[key] = Number(value ?? 0)
  }
}

function getHalfPreviewPoint(event: PointerEvent): { x: number; y: number } | null {
  const stage = halfPreviewContainerRef.value?.querySelector<HTMLElement>('.half-preview-stage')
  if (!stage) return null
  const rect = stage.getBoundingClientRect()
  const scale = previewScale.value || 1
  return {
    x: (event.clientX - rect.left) / scale,
    y: (event.clientY - rect.top) / scale,
  }
}

function handleHalfPreviewPointerDown(event: PointerEvent): void {
  if (!selectedPerson.value) return
  const point = getHalfPreviewPoint(event)
  if (!point) return
  event.preventDefault()
  halfDragState.value = {
    offsetX: point.x - Number(selectedPerson.value.half.x ?? 0),
    offsetY: point.y - Number(selectedPerson.value.half.y ?? 0),
  }
  window.addEventListener('pointermove', handleHalfPreviewPointerMove)
  window.addEventListener('pointerup', handleHalfPreviewPointerUp)
}

function handleHalfPreviewPointerMove(event: PointerEvent): void {
  const state = halfDragState.value
  if (!state) return
  const point = getHalfPreviewPoint(event)
  if (!point) return
  updateSelectedHalfNumber('x', Math.round(point.x - state.offsetX))
  updateSelectedHalfNumber('y', Math.round(point.y - state.offsetY))
}

function handleHalfPreviewPointerUp(): void {
  halfDragState.value = null
  window.removeEventListener('pointermove', handleHalfPreviewPointerMove)
  window.removeEventListener('pointerup', handleHalfPreviewPointerUp)
}

function onBasicScroll(event: Event): void {
  basicScrollTop.value = (event.target as HTMLElement).scrollTop
}

function onAdvancedScroll(event: Event): void {
  advancedScrollTop.value = (event.target as HTMLElement).scrollTop
}

async function openScriptPicker(): Promise<void> {
  await refreshCloudFiles()
  const files = currentCloudFiles.value
  if (!files.length) return
  const selected = mode.value === 'basic' ? selectedBasicCloudFile.value : selectedAdvancedCloudFile.value
  scriptPickerSelection.value = selected || files[0]?.value || ''
  scriptPickerOpen.value = true
}

async function confirmOpenScriptPicker(): Promise<void> {
  if (!scriptPickerSelection.value) {
    message.warning('请选择要打开的文本导入文件')
    return
  }
  if (mode.value === 'basic') {
    selectedBasicCloudFile.value = scriptPickerSelection.value
  } else {
    selectedAdvancedCloudFile.value = scriptPickerSelection.value
  }
  scriptPickerOpen.value = false
  await openCloudFile(scriptPickerSelection.value)
}

function handleSaveCloudFileClick(): void {
  const hasData = mode.value === 'basic' ? basicRows.value.length : advancedRows.value.length
  if (!hasData) {
    message.warning('请先分析文本后再保存')
    return
  }
  pendingSaveName.value = lastSaveName.value.trim() || TEXT_IMPORT_SAVE_NAME_DEFAULT
  saveNamePromptOpen.value = true
}

async function confirmSaveCloudFile(): Promise<void> {
  const name = pendingSaveName.value.trim()
  if (!name) {
    message.warning('请输入保存名称')
    return
  }
  lastSaveName.value = name
  saveNamePromptOpen.value = false
  await saveCloudFile(name)
}

async function saveCloudFile(fileNameSource: string): Promise<void> {
  const oss = getOssClient()
  if (!oss) return
  cloudLoading.value = true
  try {
    await oss.createFolder(orgtextFolderPath.value)
    const fileName = normalizeOrgtextFileName(fileNameSource)
    const objectKey = `${orgtextFolderPath.value}/${fileName}`
    await oss.writeData(objectKey, mode.value === 'basic' ? buildBasicPayload() : buildAdvancedPayload(), {
      contentType: 'application/json;charset=utf-8',
    })
    message.success(`已保存到云盘：${fileName}`)
    await refreshCloudFiles()
    if (mode.value === 'basic') {
      selectedBasicCloudFile.value = objectKey
    } else {
      selectedAdvancedCloudFile.value = objectKey
    }
  } catch (error) {
    message.error(error instanceof Error ? error.message : '保存云盘文本失败')
  } finally {
    cloudLoading.value = false
  }
}

/** 打开当前模式下匹配的 `.orgtext` 云盘文件。 */
async function openCloudFile(objectKey?: string): Promise<void> {
  const oss = getOssClient()
  if (!oss) return
  const targetKey = String(objectKey ?? '').trim()
    || (mode.value === 'basic' ? selectedBasicCloudFile.value : selectedAdvancedCloudFile.value)
  if (!targetKey) {
    message.warning('请选择要打开的文本导入文件')
    return
  }
  cloudLoading.value = true
  try {
    const text = await oss.getText(targetKey)
    const data = parseOrgtextPayload(text)
    if (!data) {
      throw new Error('invalid')
    }
    if (data.type === 'bubble') {
      message.warning('暂不支持气泡文本导入')
      return
    }
    if (mode.value === 'basic') {
      if (data.type !== 'text' || !Array.isArray(data.data)) {
        message.warning('请选择初级剧本文件')
        return
      }
      basicRows.value = data.data
      resetResultListScroll()
      selectedBasicRowId.value = basicRows.value[0]?.id ?? ''
      basicShowResult.value = true
      return
    }
    if (data.type !== 'advanced' || !Array.isArray(data.tableData)) {
      message.warning('请选择高级剧本文件')
      return
    }
    advancedRows.value = normalizeAdvancedTextImportRows(data.tableData)
    advancedText.value = data.textContent ?? ''
    advancedSettings.value = { ...advancedSettings.value, ...(data.settings ?? {}) }
    rows.value = advancedSettings.value.rows
    columns.value = advancedSettings.value.columns
    syncAdvancedSceneNameRows()
    resetResultListScroll()
    selectedAdvancedRowId.value = advancedRows.value[0]?.uid ?? ''
    advancedStep.value = 4
  } catch {
    message.error('文本导入文件解析失败')
  } finally {
    cloudLoading.value = false
  }
}
</script>

<template>
  <NModal
    :show="show"
    preset="card"
    class="text-import-modal modal-text-import"
    :class="{ 'text-import-modal--portrait': portraitLayout }"
    :mask-closable="false"
    :auto-focus="false"
    @update:show="emit('update:show', $event)"
  >
    <template #header>
      <div class="text-import-header">
        <span class="text-import-header__title">文本导入</span>
        <div class="text-import-header__tabs" aria-label="文本导入模式">
          <button type="button" :class="{ active: mode === 'basic' }" @click="mode = 'basic'">初级</button>
          <button type="button" :class="{ active: mode === 'advanced' }" @click="mode = 'advanced'">高级</button>
        </div>
      </div>
    </template>
    <input
      ref="txtFileInputRef"
      type="file"
      accept=".txt,text/plain"
      class="text-import-txt-input"
      @change="handleTxtFileChange"
    >
    <div class="text-import">
      <section v-show="mode === 'basic'" class="import-panel" :class="{ 'basic-input-panel': !basicShowResult }">
        <template v-if="!basicShowResult">
          <NInput
            v-model:value="basicText"
            class="script-input"
            type="textarea"
            placeholder="请将剧本文本粘贴到这里"
          />
          <div class="settings-grid">
            <div class="setting-block">
              <h3>设置姓名识别</h3>
              <label class="switch-row">
                <NSwitch v-model:value="basicNameIdentify" />
                <span>【姓名】+ 换行</span>
              </label>
              <p>例如：<br />【柳丁】<br />今天天气不错</p>
            </div>
            <div class="setting-block">
              <h3>设置行数和列数</h3>
              <div class="number-row">
                <span>每页</span>
                <NumberStepperField v-model="rows" :min="1" :max="100" />
                <span>行</span>
                <span>每</span>
                <NumberStepperField v-model="columns" :min="1" :max="100" />
                <span>字换行</span>
              </div>
              <div ref="previewContainerRef" class="dialog-preview" :style="previewFrameStyle">
                <div class="dialog-preview-stage" :style="previewStageStyle">
                  <img
                    v-if="talkBoxUrl"
                    class="dialog-preview-image"
                    :src="talkBoxUrl"
                    alt="对话框底图"
                    draggable="false"
                    :style="previewTalkBoxStyle"
                    @load="handleTalkImageLoad"
                  />
                  <img
                    v-if="nameBoxUrl"
                    class="dialog-preview-image"
                    :src="nameBoxUrl"
                    alt="姓名框底图"
                    draggable="false"
                    :style="previewNameBoxStyle"
                    @load="handleNameImageLoad"
                  />
                  <div class="dialog-preview-name" :style="previewNameTextStyle">姓名</div>
                  <div class="dialog-preview-text" :style="previewTalkTextStyle">
                    <div v-for="line in rows" :key="line">{{ previewText }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="result-table basic-table">
            <div class="result-table-track" :style="{ minWidth: TEXT_IMPORT_BASIC_TABLE_MIN_WIDTH }">
            <div class="table-head" :style="{ gridTemplateColumns: TEXT_IMPORT_BASIC_GRID_COLUMNS }">
              <span>剧情名</span><span>背景音乐</span><span>背景图</span><span>姓名</span><span>显示文章</span><span class="table-head-more" aria-hidden="true" />
            </div>
            <div class="virtual-list" @scroll="onBasicScroll">
              <div class="virtual-spacer" :style="{ height: `${basicTableTotalHeight}px` }">
                <TextImportResultRow
                  v-for="{ row, top, height } in basicVisibleRows"
                  :key="row.id"
                  variant="basic"
                  :basic-row="row"
                  :selected="selectedBasicRowId === row.id"
                  :style="{ transform: `translateY(${top}px)`, height: `${height}px` }"
                  @select="selectedBasicRowId = row.id"
                />
              </div>
            </div>
            </div>
          </div>
          <div class="result-count">共 {{ basicMapCount }} 个剧情，{{ basicRows.length }} 行分析段</div>
        </template>
      </section>

      <section
        v-show="mode === 'advanced'"
        class="import-panel"
        :class="{
          'advanced-input-panel': advancedStep === 1,
          'advanced-result-panel': advancedStep === 4,
        }"
      >
        <div class="steps">
          <span v-for="step in 4" :key="step" :class="{ active: advancedStep >= step }">{{ step }} {{ ADVANCED_STEP_LABELS[step - 1] }}</span>
        </div>
        <div v-if="advancedStep === 1" class="advanced-step">
          <NInput
            v-model:value="advancedText"
            class="script-input"
            type="textarea"
            placeholder="请将高级导入文本粘贴到这里"
          />
        </div>
        <div v-else-if="advancedStep === 2" class="advanced-step symbol-layout">
          <div class="symbol-recognition-grid">
            <div class="setting-block symbol-card">
              <h3>设置姓名识别</h3>
              <div class="symbol-card-body">
                <div class="symbol-two-fields">
                  <label class="symbol-field">
                    <span>前缀</span>
                    <NInput v-model:value="advancedSettings.namePrepend" size="small" maxlength="1" />
                  </label>
                  <label class="symbol-field">
                    <span>后缀</span>
                    <NInput v-model:value="advancedSettings.nameAppend" size="small" maxlength="1" />
                  </label>
                </div>
                <label class="switch-row">
                  <NSwitch v-model:value="advancedSettings.nameWrap" />
                  <span>姓名后换行</span>
                </label>
                <div class="symbol-example">
                  <span>例如：</span>
                  <p>{{ `${advancedSettings.namePrepend}姓名${advancedSettings.nameAppend}${advancedSettings.nameWrap ? '\n' : ''}今天天气不错` }}</p>
                </div>
              </div>
            </div>
            <div class="setting-block symbol-card">
              <h3>设置选项识别</h3>
              <div class="symbol-card-body symbol-card-body--option">
                <div class="option-settings">
                  <label class="switch-row">
                    <NSwitch v-model:value="advancedSettings.optionRecognize" />
                    <span>启用选项识别</span>
                  </label>
                  <div class="option-field-list">
                    <label class="symbol-field symbol-field--compact">
                      <span>开始</span>
                      <NInput v-model:value="advancedSettings.optionStart" size="small" :disabled="!advancedSettings.optionRecognize" />
                    </label>
                    <label class="symbol-field symbol-field--compact">
                      <span>选项</span>
                      <NInput v-model:value="advancedSettings.optionItem" size="small" :disabled="!advancedSettings.optionRecognize" />
                    </label>
                    <label class="symbol-field symbol-field--compact">
                      <span>结束</span>
                      <NInput v-model:value="advancedSettings.optionEnd" size="small" :disabled="!advancedSettings.optionRecognize" />
                    </label>
                  </div>
                </div>
                <div class="symbol-example">
                  <span>例如：</span>
                  <p>{{ `${advancedSettings.optionStart}\n${advancedSettings.optionItem}委婉告诉他\n请考虑一下\n${advancedSettings.optionItem}直接告诉他\n答案错误\n${advancedSettings.optionEnd}` }}</p>
                </div>
              </div>
            </div>
            <div class="setting-block symbol-card">
              <h3>设置剧情识别</h3>
              <div class="symbol-card-body">
                <label class="switch-row">
                  <NSwitch v-model:value="advancedSettings.sceneRecognize" />
                  <span>启用剧情识别</span>
                </label>
                <div class="symbol-two-fields">
                  <label class="symbol-field">
                    <span>前缀</span>
                    <NInput v-model:value="advancedSettings.scenePrepend" size="small" :disabled="!advancedSettings.sceneRecognize" maxlength="1" />
                  </label>
                  <label class="symbol-field">
                    <span>后缀</span>
                    <NInput v-model:value="advancedSettings.sceneAppend" size="small" :disabled="!advancedSettings.sceneRecognize" maxlength="1" />
                  </label>
                </div>
                <label class="switch-row">
                  <NSwitch v-model:value="advancedSettings.sceneWrap" :disabled="!advancedSettings.sceneRecognize" />
                  <span>剧情名后换行</span>
                </label>
                <div class="symbol-example">
                  <span>例如：</span>
                  <p>{{ `${advancedSettings.scenePrepend}第一章${advancedSettings.sceneAppend}${advancedSettings.sceneWrap ? '\n' : ''}新的一天开始了` }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="setting-block symbol-preview-card">
            <h3>设置行数和列数</h3>
            <div class="symbol-card-body">
              <div class="number-row">
                <span>每页</span><NumberStepperField v-model="rows" :min="1" :max="100" />
                <span>行，每</span><NumberStepperField v-model="columns" :min="1" :max="100" /><span>字</span>
              </div>
              <div ref="advancedPreviewContainerRef" class="dialog-preview dialog-preview--advanced" :style="previewFrameStyle">
                <div class="dialog-preview-stage" :style="previewStageStyle">
                  <img
                    v-if="talkBoxUrl"
                    class="dialog-preview-image"
                    :src="talkBoxUrl"
                    alt="对话框底图"
                    draggable="false"
                    :style="previewTalkBoxStyle"
                    @load="handleTalkImageLoad"
                  />
                  <img
                    v-if="nameBoxUrl"
                    class="dialog-preview-image"
                    :src="nameBoxUrl"
                    alt="姓名框底图"
                    draggable="false"
                    :style="previewNameBoxStyle"
                    @load="handleNameImageLoad"
                  />
                  <div class="dialog-preview-name" :style="previewNameTextStyle">姓名</div>
                  <div class="dialog-preview-text" :style="previewTalkTextStyle">
                    <div v-for="line in rows" :key="line">{{ previewText }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="advancedStep === 3" class="advanced-step half-bind-layout">
          <div class="half-bind-sidebar">
            <aside class="person-list">
              <button
                v-for="person in advancedPeople"
                :key="person.name"
                type="button"
                :class="{ active: selectedPersonName === person.name }"
                @click="selectedPersonName = person.name"
              >
                {{ person.name }}{{ person.half.halfPath ? '[已设置]' : '' }}
              </button>
            </aside>
            <div class="half-setting" v-if="selectedPerson">
              <ResourceSelectField :model-value="selectedPerson.half.halfPath" category="graphic" base-folder-path="立绘" store-mode="engine" display-mode="full" picker-title="选择立绘" @update:model-value="updateSelectedHalfPath" />
              <div class="compact-row">
                <button type="button" :class="{ active: selectedPerson.half.halfId === 3 }" @click="updateSelectedHalfPosition(3)">左</button>
                <button type="button" :class="{ active: selectedPerson.half.halfId === 5 }" @click="updateSelectedHalfPosition(5)">中</button>
                <button type="button" :class="{ active: selectedPerson.half.halfId === 4 }" @click="updateSelectedHalfPosition(4)">右</button>
              </div>
              <div class="number-row">
                <span>X</span><NumberStepperField :model-value="selectedPerson.half.x" @update:model-value="updateSelectedHalfNumber('x', $event)" />
                <span>Y</span><NumberStepperField :model-value="selectedPerson.half.y" @update:model-value="updateSelectedHalfNumber('y', $event)" />
              </div>
            </div>
            <div v-else class="empty-tip">没有识别到姓名</div>
          </div>
          <div ref="halfPreviewContainerRef" class="half-preview" :style="halfPreviewFrameStyle">
            <div class="half-preview-stage" :style="halfPreviewStageStyle">
              <img
                v-if="selectedHalfPreviewUrl"
                class="half-preview-image"
                :src="selectedHalfPreviewUrl"
                alt="立绘预览"
                draggable="false"
                :style="halfPreviewImageStyle"
                @load="handleHalfImageLoad"
                @pointerdown="handleHalfPreviewPointerDown"
              />
              <img
                v-if="talkBoxUrl"
                class="dialog-preview-image"
                :src="talkBoxUrl"
                alt="对话框底图"
                draggable="false"
                :style="previewTalkBoxStyle"
                @load="handleTalkImageLoad"
              />
              <img
                v-if="nameBoxUrl"
                class="dialog-preview-image"
                :src="nameBoxUrl"
                alt="姓名框底图"
                draggable="false"
                :style="previewNameBoxStyle"
                @load="handleNameImageLoad"
              />
              <div class="dialog-preview-name" :style="halfPreviewNameTextStyle">{{ halfPreviewNameText }}</div>
              <div class="dialog-preview-text" :style="previewTalkTextStyle">
                <div v-for="line in rows" :key="line">{{ previewText }}</div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="advanced-step advanced-result-step">
          <div class="result-table advanced-table">
            <div class="result-table-track" :style="{ minWidth: TEXT_IMPORT_ADVANCED_TABLE_MIN_WIDTH }">
            <div class="table-head" :style="{ gridTemplateColumns: TEXT_IMPORT_ADVANCED_GRID_COLUMNS }">
              <span>类型</span><span>剧情名</span><span>背景音乐</span><span>背景图片</span><span>姓名</span><span>文本内容</span><span class="table-head-more" aria-hidden="true" />
            </div>
            <div class="virtual-list" @scroll="onAdvancedScroll">
              <div class="virtual-spacer" :style="{ height: `${advancedTableTotalHeight}px` }">
                <TextImportResultRow
                  v-for="{ row, top, height } in advancedVisibleRows"
                  :key="row.uid"
                  variant="advanced"
                  :advanced-row="row"
                  :selected="selectedAdvancedRowId === row.uid"
                  :style="{ transform: `translateY(${top}px)`, height: `${height}px` }"
                  @select="selectedAdvancedRowId = row.uid"
                  @advanced-content-blur="updateAdvancedTextContent(row, row.textContent)"
                />
              </div>
            </div>
            </div>
          </div>
          <div class="result-count">共 {{ advancedMapCount }} 个剧情，{{ advancedRows.length }} 个分析段</div>
        </div>
      </section>

      <footer class="import-footer">
        <div class="cloud-tools">
          <NButton
            size="small"
            :disabled="cloudLoading || !canOpenCloudScript"
            @click="openScriptPicker"
          >
            打开
          </NButton>
          <NButton
            v-if="isTextImportResultStep"
            size="small"
            :disabled="cloudLoading"
            @click="handleSaveCloudFileClick"
          >
            保存
          </NButton>
        </div>
        <div class="import-footer-actions">
          <NButton v-if="isTextImportPasteStep" size="small" @click="triggerTxtFileImport">导入文本</NButton>
          <NButton v-if="!isTextImportPasteStep" size="small" @click="handleTextImportPrev">上一步</NButton>
          <NButton
            v-if="!isTextImportResultStep"
            size="small"
            type="primary"
            @click="handleTextImportNext"
          >
            下一步
          </NButton>
          <NButton
            v-else
            size="small"
            type="primary"
            :loading="importSubmitting"
            :disabled="importSubmitting"
            @click="handleTextImportConfirm"
          >
            确定
          </NButton>
        </div>
      </footer>
    </div>
    <NModal
      v-model:show="scriptPickerOpen"
      preset="card"
      title="选择"
      class="text-import-script-picker"
      :mask-closable="false"
    >
      <div class="script-picker">
        <AppSelectField
          v-model="scriptPickerSelection"
          :use-cell-style="false"
          :options="currentCloudFiles"
        />
        <div class="script-picker-actions">
          <NButton size="small" @click="scriptPickerOpen = false">取消</NButton>
          <NButton
            size="small"
            type="primary"
            :disabled="cloudLoading || !scriptPickerSelection"
            :loading="cloudLoading"
            @click="confirmOpenScriptPicker"
          >
            打开
          </NButton>
        </div>
      </div>
    </NModal>
    <NModal
      v-model:show="saveNamePromptOpen"
      preset="card"
      title="保存名称"
      class="text-import-save-modal"
      :mask-closable="false"
    >
      <div class="save-name-dialog">
        <NInput v-model:value="pendingSaveName" placeholder="请输入保存名称" @keyup.enter="confirmSaveCloudFile" />
        <div class="save-name-actions">
          <NButton size="small" @click="saveNamePromptOpen = false">取消</NButton>
          <NButton size="small" type="primary" :disabled="cloudLoading" @click="confirmSaveCloudFile">保存</NButton>
        </div>
      </div>
    </NModal>
  </NModal>
</template>

<style scoped>
.text-import {
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  height: min(76vh, 720px);
  color: var(--theme-text-primary);
}

.text-import-txt-input {
  display: none;
}

.text-import-header {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.text-import-header__title {
  flex: 0 0 auto;
  color: var(--theme-text-heading);
  font-size: var(--app-font-size-lg);
  font-weight: 600;
  line-height: var(--app-line-height-tight);
}

.text-import-header__tabs {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.text-import-header__tabs button {
  height: 26px;
  padding: 0 12px;
  border: 0;
  color: var(--theme-text-secondary);
  background: var(--theme-bg-panel);
  cursor: pointer;
  font-size: var(--app-font-size-sm);
}

.text-import-header__tabs button.active {
  color: var(--theme-accent);
  background: var(--theme-bg-active);
  font-weight: 600;
}

.import-footer,
.steps,
.number-row,
.compact-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.number-row :deep(.number-stepper-field) {
  width: 88px;
  flex: 0 0 88px;
}

.import-panel {
  min-height: 0;
  padding: 8px 0 0;
  overflow: auto;
}

.basic-input-panel {
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  gap: 10px;
  overflow: hidden;
}

.advanced-input-panel {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 8px;
  overflow: hidden;
}

.advanced-result-panel {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  overflow: hidden;
}

.advanced-step {
  min-height: 0;
}

.advanced-result-step {
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  min-height: 0;
}

.script-input {
  width: 100%;
  height: 260px;
}

.basic-input-panel .script-input {
  height: 100%;
  min-height: 0;
}

.advanced-input-panel .script-input {
  height: 100%;
  min-height: 0;
}

.script-input :deep(.n-input-wrapper),
.script-input :deep(.n-input__textarea),
.script-input :deep(.n-input__textarea-el) {
  height: 100%;
}

.script-input :deep(.n-input__textarea-el) {
  resize: none;
}

.settings-grid {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 12px;
  margin-top: 12px;
}

.symbol-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  align-content: start;
}

.symbol-recognition-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.symbol-card {
  min-width: 0;
}

.symbol-preview-card {
  min-width: 0;
}

.symbol-card-body {
  display: grid;
  gap: 10px;
}

.symbol-card-body--option {
  grid-template-columns: 168px minmax(0, 1fr);
  align-items: start;
}

.option-settings {
  display: grid;
  gap: 8px;
}

.symbol-two-fields {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
}

.symbol-field {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: var(--theme-text-secondary);
  font-size: 12px;
}

.symbol-field--compact {
  grid-template-columns: 36px 88px;
}

.option-field-list {
  display: grid;
  gap: 8px;
}

.symbol-example {
  display: grid;
  gap: 4px;
  padding: 8px;
  color: var(--theme-text-secondary);
  background: var(--theme-bg-card);
  font-size: 12px;
}

.symbol-example span {
  color: var(--theme-text-heading);
}

.symbol-example p {
  margin: 0;
  white-space: pre-line;
}

.setting-block {
  padding: 10px;
  background: var(--theme-bg-panel);
}

.setting-block h3 {
  margin: 0 0 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--theme-border);
  font-size: 13px;
}

.setting-block p {
  margin: 8px 0 0;
  color: var(--theme-text-secondary);
  font-size: 12px;
  white-space: pre-line;
}

.setting-block input {
  width: 100%;
  min-width: 0;
  height: 28px;
  padding: 4px 6px;
  border: 1px solid var(--theme-border);
  color: var(--theme-text-heading);
  background: var(--theme-bg-card);
  box-sizing: border-box;
  font: inherit;
}

.dialog-preview {
  position: relative;
  margin-top: 10px;
  color: var(--theme-text-heading);
  background: var(--theme-bg-raised);
  overflow: hidden;
}

.dialog-preview--advanced {
  margin-top: 0;
}

.dialog-preview-stage {
  position: absolute;
  left: 50%;
  transform-origin: center top;
}

.dialog-preview-image,
.dialog-preview-name,
.dialog-preview-text {
  position: absolute;
  box-sizing: border-box;
}

.dialog-preview-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: fill;
}

.dialog-preview-name,
.dialog-preview-text {
  overflow: hidden;
  white-space: nowrap;
}

.result-count,
.empty-tip {
  color: var(--theme-text-secondary);
  font-size: 12px;
}

.result-table {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow-x: auto;
  overflow-y: hidden;
}

.result-table-track {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  width: 100%;
  min-height: 0;
}

.table-head {
  position: relative;
  z-index: 2;
  display: grid;
  gap: 1px;
  align-items: center;
  flex-shrink: 0;
  height: 30px;
  color: var(--theme-text-heading);
  background: var(--theme-bg-panel);
  font-size: 12px;
  font-weight: 600;
}

.table-head span {
  padding: 0 6px;
}

.virtual-list {
  min-height: 0;
  overflow: auto;
  background: var(--theme-bg-card);
}

.basic-table .virtual-list {
  height: 510px;
}

.advanced-result-step .virtual-list {
  height: auto;
}

.virtual-spacer {
  position: relative;
  width: 100%;
}

.steps {
  padding-bottom: 6px;
  color: var(--theme-text-secondary);
  font-size: 12px;
}

.steps span {
  padding: 4px 8px;
  background: var(--theme-bg-panel);
}

.steps span.active {
  color: var(--theme-accent);
  font-weight: 600;
}

.half-bind-layout {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 12px;
  min-height: 0;
}

.half-bind-sidebar {
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  gap: 12px;
  min-height: 0;
}

.person-list button,
.compact-row button {
  height: 28px;
  padding: 0 14px;
  border: 0;
  color: var(--theme-text-secondary);
  background: var(--theme-bg-panel);
  cursor: pointer;
}

.person-list button.active,
.compact-row button.active {
  color: var(--theme-accent);
  background: var(--theme-bg-active);
  font-weight: 600;
}

.person-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 430px;
  overflow: auto;
}

.half-setting {
  display: grid;
  gap: 12px;
  align-content: start;
  padding: 12px;
  background: var(--theme-bg-panel);
}

.half-preview {
  position: relative;
  min-height: 0;
  background: var(--theme-bg-raised);
  overflow: hidden;
}

.half-preview-stage {
  position: absolute;
  left: 50%;
  top: 0;
  transform-origin: center top;
  background: var(--theme-bg-card);
}

.half-preview-image {
  position: absolute;
  display: block;
  object-fit: contain;
  box-sizing: border-box;
  touch-action: none;
  user-select: none;
}

.import-footer {
  justify-content: flex-end;
  padding-top: 10px;
  border-top: 1px solid var(--theme-border);
  flex-wrap: wrap;
}

.cloud-tools {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  margin-right: auto;
}

.import-footer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.script-picker {
  display: grid;
  gap: 12px;
}

.script-picker-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.save-name-dialog {
  display: grid;
  gap: 12px;
}

.save-name-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

@media (orientation: portrait), (max-width: 760px) {
  .text-import {
    height: 82vh;
  }

  .settings-grid,
  .symbol-layout,
  .symbol-recognition-grid,
  .half-bind-layout {
    grid-template-columns: 1fr;
  }

  .half-bind-sidebar {
    grid-template-rows: auto auto;
  }

  .symbol-card-body--option {
    grid-template-columns: 1fr;
  }

  .symbol-field--compact {
    grid-template-columns: 42px minmax(0, 1fr);
  }

  .import-footer {
    justify-content: flex-start;
    overflow-x: auto;
  }

  .cloud-tools {
    flex: 1 0 100%;
  }

  .result-table {
    overflow-x: auto;
  }
}

:global(body .text-import-modal.n-modal) {
  width: min(980px, 96vw);
  max-width: 96vw;
  box-sizing: border-box;
  overflow: hidden;
}

:global(body .text-import-modal .n-card-header) {
  border-bottom: 1px solid var(--theme-border);
  padding-top: 8px;
  padding-bottom: 6px;
  min-height: 0;
}

:global(body .text-import-modal .n-card-header__main) {
  width: 100%;
  font-size: var(--app-font-size-lg);
  line-height: var(--app-line-height-tight);
}

:global(body .text-import-modal .n-card__content) {
  padding: 8px 16px 10px !important;
}

:global(body .text-import-modal--portrait.n-modal) {
  width: 100vw;
  max-width: 100vw;
  height: 100vh;
  max-height: 100vh;
}

:global(body .text-import-save-modal.n-modal) {
  width: min(360px, 92vw);
}

:global(body .text-import-script-picker.n-modal) {
  width: min(360px, 92vw);
}

:global(body .text-import-script-picker .n-card-header) {
  display: flex;
  align-items: center;
  padding: 4px 8px 4px 12px !important;
  min-height: 0 !important;
  border-bottom: 1px solid var(--theme-border);
}

:global(body .text-import-script-picker .n-card-header__main) {
  font-size: var(--app-font-size-md);
  line-height: 1;
  padding: 0;
}

:global(body .text-import-script-picker .n-card-header__extra) {
  display: flex;
  align-items: center;
  padding: 0;
  margin: 0;
}

:global(body .text-import-script-picker .n-base-close) {
  width: 24px;
  height: 24px;
  margin: 0;
  padding: 0;
}

:global(body .text-import-script-picker .n-card__content) {
  padding: 10px 12px 12px !important;
}
</style>
